import React, { useState } from 'react';
import {
  IconUser, IconPencil, IconCheck, IconAlertCircle,
  IconEye, IconEyeOff, IconCamera,
  IconLayout, IconSun, IconMoon, IconDeviceLaptop,
  IconArrowsMaximize, IconLayoutColumns, IconScreenShare, IconDeviceDesktop, IconMaximize,
  IconTextResize,
} from '@tabler/icons-react';
import api from '../api/api';
import { useRef } from 'react';
import { applySettings, loadSettings } from '../components/ThemeSettings';

const EDITOR_TYPES = [
  {
    value: 'jodit',
    label: 'Jodit 에디터',
    description: '기본 에디터. 다양한 기능을 제공하는 강력한 에디터입니다.',
    preview: (
      <div style={{ border: '1px solid #e6e7e9', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
        {/* 툴바 */}
        <div style={{
          background: '#f8f9fa', borderBottom: '1px solid #e6e7e9',
          padding: '6px 8px', display: 'flex', gap: 4, flexWrap: 'wrap',
        }}>
          {['B', 'I', 'U', '|', 'A', 'Aa', '|', '≡', '⋮', '|', '⊕'].map((btn, i) => (
            btn === '|'
              ? <div key={i} style={{ width: 1, background: '#dee2e6', margin: '0 2px' }} />
              : <button key={i} style={{
                  background: 'none', border: '1px solid #dee2e6',
                  borderRadius: 3, padding: '2px 6px', fontSize: 11,
                  cursor: 'default', color: '#495057',
                }}>{btn}</button>
          ))}
        </div>
        {/* 에디터 영역 */}
        <div style={{ padding: '12px 14px', minHeight: 60, color: '#868e96', fontSize: 13 }}>
          내용을 입력하세요...
        </div>
        {/* 상태바 */}
        <div style={{
          background: '#f8f9fa', borderTop: '1px solid #e6e7e9',
          padding: '3px 8px', fontSize: 10, color: '#adb5bd',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>Jodit Rich Text Editor</span>
          <span>0 words</span>
        </div>
      </div>
    ),
  },
  {
    value: 'summernote',
    label: 'Summernote 에디터',
    description: 'Bootstrap 기반의 심플하고 직관적인 에디터 (AdminLTE 스타일).',
    preview: (
      <div style={{ border: '1px solid #e6e7e9', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
        {/* 툴바 */}
        <div style={{
          background: '#f8f9fa', borderBottom: '1px solid #e6e7e9',
          padding: '6px 8px', display: 'flex', gap: 4, flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          {[
            { label: 'B', style: { fontWeight: 'bold' } },
            { label: 'I', style: { fontStyle: 'italic' } },
            { label: 'U', style: { textDecoration: 'underline' } },
            { label: '|' },
            { label: '≡' }, { label: '⋮' },
            { label: '|' },
            { label: '🔗' }, { label: '🖼' },
            { label: '|' },
            { label: '<>' },
          ].map((btn, i) =>
            btn.label === '|'
              ? <div key={i} style={{ width: 1, height: 16, background: '#dee2e6', margin: '0 2px' }} />
              : <button key={i} style={{
                  background: 'none', border: '1px solid #dee2e6',
                  borderRadius: 3, padding: '2px 6px', fontSize: 11,
                  cursor: 'default', color: '#495057',
                  ...btn.style,
                }}>{btn.label}</button>
          )}
        </div>
        {/* 에디터 영역 */}
        <div style={{ padding: '12px 14px', minHeight: 60, color: '#868e96', fontSize: 13 }}>
          내용을 입력하세요...
        </div>
      </div>
    ),
  },
];

export default function AccountSettingsPage({ me, showToast, onUpdated, onEditorTypeChange }) {
  /* ── 탭 ── */
  const [activeSection, setActiveSection] = useState('editor');

  /* ── 에디터 설정 ── */
  const [editorType, setEditorType] = useState(
    () => localStorage.getItem('preferredEditor') || 'jodit'
  );

  const handleSelectEditor = (type) => {
    setEditorType(type);
    onEditorTypeChange?.(type);
    showToast('에디터 설정이 저장되었습니다.', 'success');
  };

  /* ── 프로필 수정 ── */
  const [nickname, setNickname]                 = useState(me?.nickname || '');
  const [password, setPassword]                 = useState('');
  const [passwordConfirm, setPasswordConfirm]   = useState('');
  const [showPw, setShowPw]                     = useState(false);
  const [showPwConfirm, setShowPwConfirm]       = useState(false);
  const [preview, setPreview]                   = useState(me?.profileImage || null);
  const [imageBase64, setImageBase64]           = useState(undefined);
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState('');
  const [success, setSuccess]                   = useState(false);
  const fileRef                                 = useRef(null);

  const initials = (me?.nickname || me?.name || me?.email || 'A')[0].toUpperCase();

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('이미지는 2MB 이하만 업로드 가능합니다.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setPreview(ev.target.result); setImageBase64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async () => {
    setError('');
    if (password && password !== passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
    if (password && password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    setSaving(true);
    try {
      const body = { nickname };
      if (password) body.password = password;
      if (imageBase64 !== undefined) body.profileImage = imageBase64;
      const res = await api.patch('/auth/me', body);
      setSuccess(true);
      onUpdated?.(res.data);
      setPassword(''); setPasswordConfirm('');
      setTimeout(() => setSuccess(false), 2000);
      showToast('프로필이 저장되었습니다.');
    } catch {
      setError('저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  /* ── 레이아웃 설정 ── */
  const [layoutSettings, setLayoutSettings] = useState(() => loadSettings());

  const updateLayout = (key, value) => {
    const next = { ...layoutSettings, [key]: value };
    setLayoutSettings(next);
    localStorage.setItem('tabler-theme', JSON.stringify(next));
    applySettings(next);
  };

  const THEME_OPTIONS = [
    {
      value: 'auto',
      label: '자동',
      desc: 'OS 설정에 따름',
      icon: <IconDeviceLaptop size={28} />,
    },
    {
      value: 'light',
      label: '화이트',
      desc: '밝은 화이트 테마',
      icon: <IconSun size={28} />,
    },
    {
      value: 'dark',
      label: '다크',
      desc: '어두운 다크 테마',
      icon: <IconMoon size={28} />,
    },
  ];

  const WIDTH_OPTIONS = [
    { value: '1320px', label: '1320', desc: '기본', icon: <IconLayoutColumns size={24} /> },
    { value: '1600px', label: '1600', desc: '넓게', icon: <IconScreenShare size={24} /> },
    { value: '1920px', label: '1920', desc: 'Full HD', icon: <IconDeviceDesktop size={24} /> },
    { value: '2560px', label: '2560', desc: '2K', icon: <IconMaximize size={24} /> },
    { value: '100%',   label: '100%', desc: '제한없음', icon: <IconArrowsMaximize size={24} /> },
  ];

  const sections = [
    { id: 'profile', icon: IconUser, label: '프로필' },
    { id: 'editor',  icon: IconPencil, label: '에디터' },
    { id: 'layout',  icon: IconLayout, label: '레이아웃' },
  ];

  return (
    <div className="page-body">
      <div className="container-xl">

        <div className="mb-3">
          <div className="text-muted small">설정</div>
          <h2 className="mb-0" style={{ fontSize: 20, fontWeight: 700 }}>계정 설정</h2>
        </div>

        <div className="row g-3">

          {/* ── 좌측 네비 ── */}
          <div className="col-12 col-md-3">
            <div className="card">
              <div className="card-body p-2">
                <ul className="nav nav-pills flex-column gap-1">
                  {sections.map(sec => {
                    const Icon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <li key={sec.id} className="nav-item">
                        <button
                          className={`nav-link d-flex align-items-center gap-2 w-100 text-start${isActive ? ' active' : ''}`}
                          onClick={() => setActiveSection(sec.id)}
                          style={{ border: 'none', background: 'none', padding: '8px 12px', borderRadius: 6 }}
                        >
                          <Icon size={16} />
                          {sec.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          {/* ── 우측 콘텐츠 ── */}
          <div className="col-12 col-md-9">

            {/* ────── 프로필 섹션 ────── */}
            {activeSection === 'profile' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title mb-0">프로필 수정</h3>
                </div>
                <div className="card-body">

                  {error && (
                    <div className="alert alert-danger mb-4" style={{ borderRadius: 8, border: 'none' }}>
                      <div className="d-flex align-items-center gap-2">
                        <IconAlertCircle size={16} style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 14 }}>{error}</span>
                      </div>
                    </div>
                  )}
                  {success && (
                    <div className="alert alert-success mb-4" style={{ borderRadius: 8, border: 'none' }}>
                      <div className="d-flex align-items-center gap-2">
                        <IconCheck size={16} style={{ flexShrink: 0 }} />
                        <span style={{ fontSize: 14 }}>저장되었습니다!</span>
                      </div>
                    </div>
                  )}

                  {/* 프로필 이미지 */}
                  <div className="mb-4 d-flex align-items-center gap-4">
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      {preview ? (
                        <img src={preview} alt="profile"
                          style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                            border: '3px solid var(--tblr-primary, #206bc4)' }} />
                      ) : (
                        <div style={{ width: 80, height: 80, borderRadius: '50%',
                          background: 'var(--tblr-primary, #206bc4)', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 30, fontWeight: 700 }}>
                          {initials}
                        </div>
                      )}
                      <button onClick={() => fileRef.current?.click()}
                        style={{ position: 'absolute', bottom: 0, right: 0,
                          width: 26, height: 26, borderRadius: '50%',
                          background: 'var(--tblr-primary, #206bc4)',
                          border: '2px solid #fff', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff' }}>
                        <IconCamera size={12} />
                      </button>
                    </div>
                    <div>
                      <div className="fw-semibold mb-1">{me?.name || '이름 없음'}</div>
                      <div className="text-muted small mb-2">{me?.email}</div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-secondary"
                          onClick={() => fileRef.current?.click()} style={{ fontSize: 12 }}>
                          이미지 변경
                        </button>
                        {preview && (
                          <button className="btn btn-sm btn-outline-danger"
                            onClick={() => { setPreview(null); setImageBase64(''); }}
                            style={{ fontSize: 12 }}>삭제</button>
                        )}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>JPG, PNG, GIF · 최대 2MB</div>
                    </div>
                    <input ref={fileRef} type="file" accept="image/*"
                      style={{ display: 'none' }} onChange={handleImageChange} />
                  </div>

                  {/* 아이디 */}
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: 14 }}>아이디</label>
                    <input type="text" className="form-control form-control-sm" value={me?.email || ''} disabled
                      style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)', fontSize: 14 }} />
                  </div>

                  {/* 이름 (읽기 전용) */}
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: 14 }}>이름</label>
                    <input type="text" className="form-control form-control-sm" value={me?.name || ''} disabled
                      style={{ background: 'var(--tblr-bg-surface-secondary, #f4f6fa)', fontSize: 14 }} />
                  </div>

                  {/* 닉네임 */}
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: 14 }}>
                      닉네임
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(없으면 이름으로 표시)</span>
                    </label>
                    <input type="text" className="form-control form-control-sm"
                      placeholder="닉네임을 입력하세요"
                      value={nickname}
                      onChange={e => setNickname(e.target.value)}
                      style={{ fontSize: 14 }} />
                  </div>

                  {/* 새 비밀번호 */}
                  <div className="mb-3">
                    <label className="form-label fw-medium" style={{ fontSize: 14 }}>
                      새 비밀번호 <span className="text-muted fw-normal ms-1" style={{ fontSize: 12 }}>(변경 시에만 입력)</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPw ? 'text' : 'password'} className="form-control form-control-sm"
                        placeholder="새 비밀번호 (6자 이상)"
                        value={password} onChange={e => setPassword(e.target.value)}
                        style={{ paddingRight: 40, fontSize: 14 }} />
                      <button type="button" onClick={() => setShowPw(v => !v)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9aa0ac' }}>
                        {showPw ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* 비밀번호 확인 */}
                  <div className="mb-1">
                    <label className="form-label fw-medium" style={{ fontSize: 14 }}>비밀번호 확인</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPwConfirm ? 'text' : 'password'} className="form-control form-control-sm"
                        placeholder="비밀번호를 다시 입력하세요"
                        value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)}
                        style={{
                          paddingRight: 40, fontSize: 14,
                          borderColor: passwordConfirm && password !== passwordConfirm
                            ? 'var(--tblr-danger, #d63939)' : undefined,
                        }} />
                      <button type="button" onClick={() => setShowPwConfirm(v => !v)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', color: '#9aa0ac' }}>
                        {showPwConfirm ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                      </button>
                    </div>
                    {passwordConfirm && password !== passwordConfirm && (
                      <div className="text-danger mt-1" style={{ fontSize: 12 }}>비밀번호가 일치하지 않습니다.</div>
                    )}
                  </div>
                </div>

                <div className="card-footer d-flex justify-content-end">
                  <button className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                    onClick={handleSaveProfile} disabled={saving}>
                    {saving
                      ? <span className="spinner-border spinner-border-sm" />
                      : <IconCheck size={16} />}
                    저장
                  </button>
                </div>
              </div>
            )}

            {/* ────── 에디터 섹션 ────── */}
            {activeSection === 'editor' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title mb-0">에디터 설정</h3>
                  <div className="card-options text-muted small">
                    게시물 작성 시 사용할 에디터를 선택하세요
                  </div>
                </div>
                <div className="card-body">

                  <div className="row g-3">
                    {EDITOR_TYPES.map(editor => {
                      const isSelected = editorType === editor.value;
                      return (
                        <div key={editor.value} className="col-12 col-lg-6">
                          <div
                            onClick={() => handleSelectEditor(editor.value)}
                            style={{
                              border: isSelected
                                ? '2px solid var(--tblr-primary, #206bc4)'
                                : '2px solid var(--tblr-border-color, #e6e7e9)',
                              borderRadius: 10,
                              padding: 16,
                              cursor: 'pointer',
                              background: isSelected
                                ? 'rgba(32,107,196,0.04)'
                                : 'var(--tblr-bg-surface, #fff)',
                              transition: 'border-color 0.15s, background 0.15s',
                              position: 'relative',
                            }}
                          >
                            {/* 선택 뱃지 */}
                            {isSelected && (
                              <span className="badge bg-primary"
                                style={{ position: 'absolute', top: 10, right: 10 }}>
                                <IconCheck size={11} className="me-1" />현재 선택
                              </span>
                            )}

                            {/* 제목 */}
                            <div className="fw-semibold mb-1" style={{ fontSize: 15 }}>
                              {editor.label}
                            </div>
                            <div className="text-muted mb-3" style={{ fontSize: 13, lineHeight: 1.5 }}>
                              {editor.description}
                            </div>

                            {/* 미리보기 */}
                            <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
                              {editor.preview}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                </div>
              </div>
            )}

            {/* ────── 레이아웃 섹션 ────── */}
            {activeSection === 'layout' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title mb-0">레이아웃 설정</h3>
                  <div className="card-options text-muted small">
                    화면 테마, 컴포넌트 크기, 가로 사이즈를 설정하세요
                  </div>
                </div>
                <div className="card-body">

                  {/* 테마 */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-3" style={{ fontSize: 15 }}>테마</label>
                    <div className="row g-3">
                      {THEME_OPTIONS.map(opt => {
                        const isSelected = layoutSettings.theme === opt.value;
                        return (
                          <div key={opt.value} className="col-12 col-sm-4">
                            <div
                              onClick={() => updateLayout('theme', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '20px 16px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 8, right: 8 }}>
                                  <IconCheck size={11} className="me-1" />선택
                                </span>
                              )}
                              <div style={{ color: isSelected ? 'var(--tblr-primary, #206bc4)' : 'var(--tblr-muted, #9aa0ac)', marginBottom: 8 }}>
                                {opt.icon}
                              </div>
                              <div className="fw-semibold mb-1" style={{ fontSize: 15 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* 컴포넌트 크기 */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: 15 }}>
                      <IconTextResize size={18} className="me-1" style={{ verticalAlign: '-3px' }} />
                      컴포넌트 크기
                    </label>
                    <div className="text-muted small mb-3">입력, 버튼, 셀렉트 등의 크기를 조절합니다</div>
                    <div className="row g-2 row-cols-5">
                      {[
                        { value: 'xs', label: 'XS', desc: '매우 작게' },
                        { value: 'sm', label: 'SM', desc: '작게' },
                        { value: 'default', label: '보통', desc: '기본' },
                        { value: 'lg', label: 'LG', desc: '크게' },
                        { value: 'xl', label: 'XL', desc: '매우 크게' },
                      ].map(opt => {
                        const isSelected = (layoutSettings.componentSize || 'sm') === opt.value;
                        return (
                          <div key={opt.value} className="col">
                            <div
                              onClick={() => updateLayout('componentSize', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '14px 8px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 10 }}>
                                  <IconCheck size={10} />
                                </span>
                              )}
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* 가로 사이즈 */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold mb-3" style={{ fontSize: 15 }}>가로 사이즈</label>
                    <div className="row g-2 row-cols-5">
                      {WIDTH_OPTIONS.map(opt => {
                        const isSelected = layoutSettings.maxWidth === opt.value;
                        return (
                          <div key={opt.value} className="col">
                            <div
                              onClick={() => updateLayout('maxWidth', opt.value)}
                              style={{
                                border: isSelected
                                  ? '2px solid var(--tblr-primary, #206bc4)'
                                  : '2px solid var(--tblr-border-color, #e6e7e9)',
                                borderRadius: 10,
                                padding: '14px 8px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                background: isSelected
                                  ? 'rgba(32,107,196,0.06)'
                                  : 'var(--tblr-bg-surface, #fff)',
                                transition: 'border-color 0.15s, background 0.15s',
                                position: 'relative',
                              }}
                            >
                              {isSelected && (
                                <span className="badge bg-primary"
                                  style={{ position: 'absolute', top: 6, right: 6, fontSize: 10 }}>
                                  <IconCheck size={10} />
                                </span>
                              )}
                              <div style={{ color: isSelected ? 'var(--tblr-primary, #206bc4)' : 'var(--tblr-muted, #9aa0ac)', marginBottom: 6 }}>
                                {opt.icon}
                              </div>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>{opt.label}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{opt.desc}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* 너비 프리뷰 바 */}
                    <div className="mt-3 rounded overflow-hidden"
                      style={{ height: 6, background: 'var(--tblr-border-color, #e6e7e9)' }}>
                      <div style={{
                        height: '100%',
                        background: 'var(--tblr-primary, #206bc4)',
                        width: ({'1320px':'30%','1600px':'45%','1920px':'60%','2560px':'80%','100%':'100%'})[layoutSettings.maxWidth] || '30%',
                        transition: 'width 0.3s ease',
                        borderRadius: 3,
                      }} />
                    </div>
                    <div className="text-muted small mt-1 text-center">
                      {layoutSettings.maxWidth === '100%' ? '화면 전체 너비 사용' : `최대 ${layoutSettings.maxWidth} 사용`}
                    </div>
                  </div>

                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
